import classNames from 'classnames';
import { m } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './show-more.module.scss';

type GapSizeOptions = 'small' | 'medium' | 'large';

export interface ShowMoreProps {
  /** The content to collapse */
  children: React.ReactNode;
  /** The class name to apply */
  className?: string;
  /** The gap between items */
  gap?: GapSizeOptions;
  /** The maximum height of the content */
  height?: number;
}

const gapVariants: Record<GapSizeOptions, string> = {
  large: styles.hasLargeGap,
  medium: styles.hasMediumGap,
  small: styles.hasSmallGap,
};

const ShowMore = ({
  children,
  className,
  gap = 'large',
  height,
}: ShowMoreProps) => {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    const isOverflowing =
      containerRef.current.scrollHeight > containerRef.current.clientHeight;

    setHasOverflow(isOverflowing);
  }, [containerRef]);

  const handleToggleExpansion = () => {
    setIsExpanded(previous => !previous);
  };

  return (
    <div
      className={classNames(styles.component, gapVariants[gap], className, {
        [styles.hasOverflow]: hasOverflow,
        [styles.isExpanded]: isExpanded,
      })}
    >
      <m.div
        ref={containerRef}
        animate={{ height: isExpanded ? 'auto' : height }}
        className={styles.content}
        initial={false}
      >
        {children}
      </m.div>

      {hasOverflow && (
        <button className={styles.viewMore} onClick={handleToggleExpansion}>
          <Typography className={styles.viewMoreText} tagStyle="bodyMedium">
            {isExpanded
              ? `- ${t('items.common.words.showLess')}`
              : `+ ${t('items.common.words.showMore')}`}
          </Typography>
        </button>
      )}
    </div>
  );
};

export default ShowMore;
